/* eslint-disable require-jsdoc */
import * as React from 'react';
import ky from 'ky';
import {toast} from 'react-toastify';

import {ThemeProvider, createTheme} from '@mui/material';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Switch from '@mui/material/Switch';

import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import Modal from '@mui/material/Modal';

import CustomMaterialTable from '../../CustomMaterialTable';

interface RowProps {
  row: PACSCharges.RowData,
}

const lambdaComercialNameDictionary = {
  'drnuvem-backoffice': {
    name: 'Backend PACS',
    description: 'Serviço que atende a interface de login, envio de estudo, listagem de estudo, laudo e etc.',
  },
  'pacs-nuvem-s3': {
    name: 'Pacs Nuvem para S3',
    description: 'Serviço que enfileira o envio de DICOM para processamento.',
  },
} as Record<string, {
  name: string,
  description: string
}>;

const calculateLambdaCost = (
    functionName: string,
    count: number,
    totalDurationInMs: number,
): number => {
  const lambdaMemoryConfigDictionary = {
    'drnuvem-backoffice': 3072,
    'pacs-nuvem-s3': 128,
  } as Record<string, number>;

  if (!lambdaMemoryConfigDictionary?.[functionName]) return 0;

  const gigabyteLambdaFactor = 0.0009765625;
  const costPerGigabyteSecond = 0.0000166667;

  const gigabytePerSecondCost = count *
  (totalDurationInMs / 1000) *
  lambdaMemoryConfigDictionary[functionName] *
  gigabyteLambdaFactor *
  costPerGigabyteSecond;

  const costPerRequest = 0.0000002;

  const totalRequestCost = count * costPerRequest;

  return Number(
      (gigabytePerSecondCost + totalRequestCost).toFixed(2),
  );
};

const calculateTotalLambdaCost = (
    row: PACSCharges.RowData,
) => {
  let totalLambdaCost = 0;

  for (const lambdaFunctionName in row.lambdaUsage) {
    if (lambdaComercialNameDictionary?.[lambdaFunctionName]) {
      totalLambdaCost += calculateLambdaCost(
          lambdaFunctionName,
          row.lambdaUsage[lambdaFunctionName].count,
          row.lambdaUsage[lambdaFunctionName].totalDurationInMs,
      );
    }
  }

  return totalLambdaCost;
};

const calculateElasticSearchCost = (
    row: PACSCharges.RowData,
): number => {
  return Number((100 / row.rowsLength).toFixed(2));
};

const calculateTotalCost = (
    row: PACSCharges.RowData,
): number => {
  const elasticSearchCost = calculateElasticSearchCost(row);
  const lambdaTotalCost = calculateTotalLambdaCost(row);

  const totalCost = (
    elasticSearchCost +
    lambdaTotalCost
  )
      .toFixed(2);

  return Number(totalCost);
};

const CostModal = (
    props: RowProps,
) => {
  const {
    row,
  } = props;

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const {lambdaUsage} = row;

  const elasticSearchCost = calculateElasticSearchCost(row);
  const lambdaTotalCost = calculateTotalLambdaCost(row);

  const totalCost = calculateTotalCost(row);

  return (
    <>
      <IconButton
        aria-label="expand row"
        size="small"
        onClick={() => handleOpen()}
      >
        <RequestQuoteIcon/>
      </IconButton>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 1000,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h4" gutterBottom component="div">
            Custos detalhados: {row.name}
          </Typography>
          <Table size="small" aria-label="purchases">
            <TableHead>
              <TableRow>
                <TableCell>Serviço</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="right">Valor(U$)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow key={`elastic-${row.id}`}>
                <TableCell component="th" scope="row">
                    (AWS) EC2/ElasticSearch
                </TableCell>
                <TableCell>
                    Servidor de buscas e Dashboard
                    (Valor rateado entre os clientes, custo fixo de U$ 100)
                </TableCell>
                <TableCell align="right">
                  {elasticSearchCost}
                </TableCell>
              </TableRow>
              <TableRow key={`lambda-${row.id}`}>
                <TableCell component="th" scope="row">
                    (AWS) Lambda
                </TableCell>
                <TableCell>
                    Processamentos assíncronos de todas as funcionalidades PACS.
                </TableCell>
                <TableCell align="right">
                  {lambdaTotalCost}
                </TableCell>
              </TableRow>
              <TableRow key={`lambda-${row.id}-detailed`}>
                <TableCell>
                  <Table size="small" aria-label="lambdaDetailed">
                    <TableHead>
                      <TableRow>
                        <TableCell>Lambda</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell align="right">Valor(U$)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {
                        Object
                            .keys(lambdaUsage)
                            .filter((lambdaFunctionName) => !!lambdaComercialNameDictionary?.[lambdaFunctionName])
                            .map((lambdaFunctionName) => {
                              return (
                                <>
                                  <TableRow key={`lambda-${row.id}-${lambdaFunctionName}`}>
                                    <TableCell component="th" scope="row">
                                      {lambdaComercialNameDictionary[lambdaFunctionName].name}
                                    </TableCell>
                                    <TableCell>
                                      {lambdaComercialNameDictionary[lambdaFunctionName].description}
                                    </TableCell>
                                    <TableCell align="right">
                                      {
                                        calculateLambdaCost(
                                            lambdaFunctionName,
                                            lambdaUsage[lambdaFunctionName].count,
                                            lambdaUsage[lambdaFunctionName].totalDurationInMs,
                                        )
                                      }
                                    </TableCell>
                                  </TableRow>
                                </>
                              );
                            })
                      }
                    </TableBody>
                  </Table>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow key={`gcp-${row.id}`}>
                <TableCell component="th" scope="row">
                    (GCP) Firebase
                </TableCell>
                <TableCell>
                  Sustentação: armazenamento, processamento e banco de dados.
                  <Typography style={{fontWeight: 'bold'}}>
                    OBS: necessário ratear valor entre clientes
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Link
                    target="_blank"
                    rel="noopener"
                    // eslint-disable-next-line max-len
                    href={`https://console.cloud.google.com/billing/010E1A-17983E-137940/reports;projects=drnuvem-prod?organizationId=719282408702&supportedpurview=project`}>
                    Disponível apenas no painel GCP
                  </Link>
                </TableCell>
              </TableRow>
              <TableRow key={`totalCost-${row.id}`}>
                <TableCell component="th" scope="row">
                  <Typography style={{fontWeight: 'bold'}}>
                    TOTAL
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography style={{fontWeight: 'bold'}}>
                    Valor somado dos custos de infraestrutura
                    (GCP precisa ser feito manualmente por limitação do próprio GCP)
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography style={{fontWeight: 'bold'}}>
                    {totalCost}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Modal>
    </>
  );
};
interface PACSTableChargesProps {
    rows: PACSCharges.RowData[],
    fetchAccounts: () => Promise<void>
}

const PACSTableCharges = (
    props: PACSTableChargesProps,
) => {
  const {
    rows,
    fetchAccounts,
  } = props;

  const handleDeleteAccount = async (row: PACSCharges.RowData) => {
    if (row.deletedAt === 'Ativo') {
      await ky.delete(
          `${process.env.REACT_APP_PACS_BACKEND_API_URL}/accounts/${row.id}`,
          {

            headers: {
              access_token: process.env.REACT_APP_PACS_BACKEND_API_ROOT_KEY,
            },
          },
      )
          .then(() => {
            toast.success(`${row.name} foi inativado com sucesso`, {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          })
          .catch((e) => {
            toast.error(`Houve um erro ao inativar ${row.name}`, {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });

            console.error(e);
          });
    } else {
      await ky.patch(
          `${process.env.REACT_APP_PACS_BACKEND_API_URL}/accounts/${row.id}/activate`,
          {
            headers: {
              access_token: process.env.REACT_APP_PACS_BACKEND_API_ROOT_KEY,
            },
          },
      )
          .then(() => {
            toast.success(`${row.name} foi ativado com sucesso`, {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          })
          .catch((e) => {
            toast.error(`Houve um erro ao ativar ${row.name}`, {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });

            console.error(e);
          });
    }

    fetchAccounts();
  };

  const columns = [
    {
      title: 'Nome do cliente',
      field: 'name',
      type: 'string',
      sorting: true,
      defaultSort: 'asc',
    },
    {
      title: 'ID do cliente',
      field: 'id',
      type: 'numeric',
      sorting: true,
    },
    {
      title: 'Nome jurídico',
      field: 'legalName',
      type: 'string',
      sorting: true,
    },
    {
      title: 'CNPJ',
      field: 'companyIdentity',
      type: 'string',
      sorting: true,
    },
    {
      title: 'Status',
      field: 'deletedAt',
      render: (row) => {
        return (
          <Switch
            checked={row.deletedAt === 'Ativo'}
            onChange={() => handleDeleteAccount(row)}
          />
        );
      },
    },
    {
      title: 'E-mail',
      field: 'notificationEmail',
      type: 'string',
      sorting: true,
    },
    {
      title: 'Tipo',
      field: 'isForHuman',
      type: 'string',
      sorting: true,
    },
    {
      title: 'Portal de resultado',
      field: 'hasResultsAccess',
      type: 'numeric',
      sorting: true,
    },
    {
      title: 'Custos',
      field: 'costs',
      render: (row) => {
        return (
          <CostModal row={row}/>
        );
      },
    },
    {
      title: '(AWS) EC2/ElasticSearch(U$)',
      field: 'awsElasticSearchCost',
      type: 'numeric',
      sorting: true,
    },
    {
      title: '(AWS) Lambda(U$)',
      field: 'awsTotalLambdaCost',
      type: 'numeric',
      sorting: true,
    },
    {
      title: 'Valor Total(U$)',
      field: 'totalCost',
      type: 'numeric',
      sorting: true,
    },
  ] as PACSCharges.Column[];

  const defaultMaterialTheme = createTheme();

  return (
    <div style={{maxWidth: '100%'}}>
      <ThemeProvider theme={defaultMaterialTheme}>
        <CustomMaterialTable
          title=""
          columns={columns}
          data={
            rows.map((row) => ({
              ...row,
              rowsLength: rows.length,
              awsElasticSearchCost: calculateElasticSearchCost(row),
              awsTotalLambdaCost: calculateTotalLambdaCost(row),
              totalCost: calculateTotalCost(row),
            }))
          }
          options={{
            exportButton: {
              csv: true,
            },
          }}
        />
      </ThemeProvider>
    </div>
  );
};

export default PACSTableCharges;
